import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { V4ThemeProvider, useV4ColorMode } from '../v4/theme';
import { RequireAuth, useShell } from '../v4/shell';
import GrowSetupPicker from '../v4/components/GrowSetupPicker';
import { ThemeMenu } from '../v4/components/ThemePicker';
import { V7_VARIANT } from './theme';

const pad2 = (n) => String(n).padStart(2, '0');

const CSS = `
@keyframes v7Blink { 0%,49%{opacity:1} 50%,100%{opacity:0} }
@keyframes v7Flicker { 0%,100%{opacity:1} 92%{opacity:1} 93%{opacity:.86} 94%{opacity:1} }
@keyframes v7Boot { from{opacity:0; transform:translateY(6px)} to{opacity:1; transform:none} }
`;

/* [ LABEL ] bracketed text button */
function Bracket({ children, onClick, active = false, title }) {
  const btn = (
    <Box component="button" onClick={onClick} sx={{
      all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap',
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      px: 0.75, py: 0.25, borderRadius: '3px',
      color: active ? 'var(--v4-bg)' : 'var(--v4-cyan)',
      background: active ? 'var(--v4-cyan)' : 'transparent',
      '&:hover': { background: active ? 'var(--v4-cyan-light)' : 'rgba(var(--v4-cyan-rgb),0.14)' },
    }}>
      [ {children} ]
    </Box>
  );
  return title ? <Tooltip title={title}>{btn}</Tooltip> : btn;
}

/* Theme list as shell output:  > standard  ~/v4 */
function renderThemeItem(v, { active, mode }) {
  const t = v.tokens[mode];
  return (
    <>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--v4-cyan)', width: 16 }}>{active ? '>' : ''}</Typography>
      <Box sx={{ width: 10, height: 10, borderRadius: '2px', background: t.cyan, boxShadow: `0 0 0 2px ${t.bg}, 0 0 0 3px rgba(var(--v4-cyan-rgb),0.4)` }} />
      <Typography sx={{ flex: 1, ml: 1.25, fontSize: '0.78rem', fontWeight: 700, textTransform: 'lowercase', color: active ? 'var(--v4-cyan)' : 'var(--v4-text)' }}>
        {v.label}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.7)', ml: 2 }}>~/{v.id}</Typography>
    </>
  );
}

function StatusLine() {
  const shell = useShell();
  const { mode, toggleMode, variant } = useV4ColorMode();
  const [themeAnchor, setThemeAnchor] = useState(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);

  return (
    <Box component="header" sx={{
      position: 'sticky', top: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap',
      px: { xs: 1.5, md: 3 }, py: 1,
      background: 'rgba(var(--v4-surface2-rgb),0.95)',
      borderBottom: '1px dashed rgba(var(--v4-cyan-rgb),0.35)',
      fontSize: '0.75rem',
    }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--v4-cyan)', letterSpacing: '0.08em', textShadow: '0 0 8px rgba(var(--v4-cyan-rgb),calc(0.6*var(--v4-glow-k)))' }}>
        ECOSCOPE://v7
      </Typography>
      <Box sx={{ minWidth: 0 }}><GrowSetupPicker /></Box>
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ display: { xs: 'none', sm: 'flex' } }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--v4-green)', boxShadow: '0 0 6px var(--v4-green)', animation: 'v7Blink 2s steps(1) infinite' }} />
        <Typography sx={{ fontSize: '0.72rem', color: 'var(--v4-green)', fontWeight: 700 }}>ONLINE</Typography>
      </Stack>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--v4-text)', display: { xs: 'none', sm: 'block' } }}>
        {pad2(now.getHours())}:{pad2(now.getMinutes())}:{pad2(now.getSeconds())}
      </Typography>
      {shell.unsavedCount > 0 && (
        <Bracket active onClick={shell.saveConfig} title="Konfiguration speichern">
          save {shell.unsavedCount}
        </Bracket>
      )}
      <Bracket onClick={e => setThemeAnchor(e.currentTarget)} title="Theme wählen">theme</Bracket>
      <Bracket onClick={toggleMode} title="Farbmodus wechseln">{mode === 'dark' ? 'paper' : 'crt'}</Bracket>
      <ThemeMenu
        anchorEl={themeAnchor} onClose={() => setThemeAnchor(null)} current={variant.id}
        renderItem={renderThemeItem}
        paperSx={{ borderRadius: '6px', background: 'rgba(var(--v4-surface2-rgb),0.97)', border: '1px dashed rgba(var(--v4-cyan-rgb),0.45)', boxShadow: '0 0 24px rgba(var(--v4-cyan-rgb),calc(0.15*var(--v4-glow-k)))' }}
        itemSx={{ py: 0.75, '&.Mui-selected': { background: 'rgba(var(--v4-cyan-rgb),0.1)' } }}
      />
      <Bracket onClick={shell.logout}>exit</Bracket>
    </Box>
  );
}

function Dock() {
  const shell = useShell();
  return (
    <Box component="nav" sx={{
      position: 'fixed', left: '50%', bottom: { xs: 10, md: 18 }, transform: 'translateX(-50%)', zIndex: 120,
      display: 'flex', gap: 0.5, p: 0.75, maxWidth: 'calc(100vw - 20px)', overflowX: 'auto',
      background: 'rgba(var(--v4-surface2-rgb),0.92)',
      border: '1px solid rgba(var(--v4-cyan-rgb),0.4)',
      borderRadius: '10px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 10px 40px rgba(var(--v4-shadow-rgb),calc(0.6*var(--v4-shadow-k))), 0 0 24px rgba(var(--v4-cyan-rgb),calc(0.15*var(--v4-glow-k)))',
      scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {shell.items.map(({ label, icon: Icon, path }, i) => {
        const active = shell.isActive(path);
        return (
          <Tooltip key={path} title={`${label}  ·  Alt+${i + 1}`} placement="top">
            <Box onClick={() => shell.go(path)} sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
              minWidth: 58, px: 1, py: 0.75, borderRadius: '6px', cursor: 'pointer',
              color: active ? 'var(--v4-bg)' : 'var(--v4-cyan)',
              background: active ? 'var(--v4-cyan)' : 'transparent',
              boxShadow: active ? '0 0 14px rgba(var(--v4-cyan-rgb),calc(0.5*var(--v4-glow-k)))' : 'none',
              transition: 'background .15s, color .15s',
              '&:hover': { background: active ? 'var(--v4-cyan)' : 'rgba(var(--v4-cyan-rgb),0.12)' },
            }}>
              <Icon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'inherit' }}>
                {i + 1}·{label.slice(0, 5)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function Prompt() {
  const shell = useShell();
  const slug = (shell.current?.path ?? '/').slice(1) || 'home';
  return (
    <Box sx={{ mb: 3, animation: 'v7Boot .35s ease both' }} key={slug}>
      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>
        {shell.username || 'grower'}@ecoscope:~$ open {slug}
      </Typography>
      <Typography component="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 800, color: 'var(--v4-cyan)', textShadow: '0 0 12px rgba(var(--v4-cyan-rgb),calc(0.45*var(--v4-glow-k)))', lineHeight: 1.2 }}>
        &gt; {(shell.current?.label ?? 'EcoScope').toUpperCase()}
        <Box component="span" sx={{ animation: 'v7Blink 1s steps(1) infinite', ml: 0.5 }}>_</Box>
      </Typography>
    </Box>
  );
}

/* Alt+1..7 jumps between pages */
function useHotkeys() {
  const shell = useShell();
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey) return;
      const n = Number(e.key);
      if (n >= 1 && n <= shell.items.length) { e.preventDefault(); shell.go(shell.items[n - 1].path); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shell]);
}

function Shell() {
  useHotkeys();
  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', animation: 'v7Flicker 8s infinite' }}>
      <style>{CSS}</style>

      {/* CRT scanlines + vignette (dark mode only, via glow-k) */}
      <Box sx={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 200, opacity: 'var(--v4-glow-k)',
        background: `
          repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 3px),
          radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)`,
      }} />

      <StatusLine />
      <Box component="main" sx={{ px: { xs: 1.5, md: 4 }, pt: 3, pb: 14, maxWidth: 1700, mx: 'auto' }}>
        <Prompt />
        <Outlet />
      </Box>
      <Dock />
    </Box>
  );
}

export default function V7Layout() {
  return (
    <V4ThemeProvider variant={V7_VARIANT}>
      <RequireAuth>
        <Shell />
      </RequireAuth>
    </V4ThemeProvider>
  );
}
